import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Search, Building2, User as UserIcon, ArrowLeft, ArrowRight, Loader2, LogIn } from 'lucide-react';
import { RootState } from '@/shared/store/store';
import { tenantsApiService, Tenant } from '@/features/tenants/api/tenants-api';
import impersonationApiService from '../api/impersonation-api';
import { startImpersonation, setLoading, setError, stopImpersonation } from '../redux/impersonation-slice';
import { loginSuccess } from '@/features/security/sagas/auth-slice';
import { sessionService } from '@/shared/services/session.service';
import { cn } from '@/shared/utils/cn';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';

interface ImpersonationFlyoutProps {
    isOpen: boolean;
    onClose: () => void;
    collapsed?: boolean;
}

import { useTrialDays } from '@/shared/hooks/useTrialDays';

export const ImpersonationFlyout: React.FC<ImpersonationFlyoutProps> = ({ isOpen, onClose, collapsed }) => {
    const dispatch = useDispatch();
    const { showError } = useGlobalNotifications();
    const { user: currentUser, token, refreshToken } = useSelector((state: RootState) => state.auth);
    const { isImpersonating, originalAdminUser, originalAdminToken, originalAdminRefreshToken } = useSelector((state: RootState) => state.impersonation);
    const trialDaysInfo = useTrialDays();
    const showTrialBanner = trialDaysInfo.isInTrial && trialDaysInfo.daysRemaining === 0;

    const [search, setSearch] = useState('');
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [tenantUsers, setTenantUsers] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Cargar tenants al abrir
    useEffect(() => {
        if (isOpen) {
            loadTenants();
        } else {
            // Reset state when closing
            setSearch('');
            setSelectedTenant(null);
            setTenantUsers([]);
        }
    }, [isOpen]);

    const loadTenants = async (query = '') => {
        setIsLoading(true);
        try {
            const response = await tenantsApiService.getTenants({ search: query, limit: 20 });
            setTenants(response.data || []);
        } catch (err) {
          console.error('Error al cargar los tenants', err);
          showError('No se pudo cargar', 'No se pudo cargar la lista de tenants. Intenta de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    const loadTenantUsers = async (tenantId: string) => {
        setIsLoading(true);
        try {
            const response = await impersonationApiService.getTenantUsers(tenantId);
            setTenantUsers(response.data?.users || []);
        } catch (err) {
          console.error('Error al cargar los usuarios del tenant', err);
          showError('No se pudo cargar', 'No se pudieron cargar los usuarios de este tenant. Intenta de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectTenant = (tenant: Tenant) => {
        setSelectedTenant(tenant);
        setSearch('');
        loadTenantUsers(tenant.id);
    };

    const handleImpersonate = async (targetUser: any) => {
        setIsLoading(true);
        try {
            const response = await impersonationApiService.impersonate(targetUser.id);
            const { token: newToken, refreshToken: newRefreshToken, user: newUser } = response.data;

            // 1. Guardar estado de impersonación (el admin actual)
            dispatch(startImpersonation({
                adminUser: currentUser,
                adminToken: token!,
                adminRefreshToken: refreshToken!,
                targetUser: newUser
            }));

            // 2. Cambiar sesión activa al nuevo usuario
            dispatch(loginSuccess({
                user: newUser,
                token: newToken,
                refreshToken: newRefreshToken
            }));

            // #619/#661 QA (CAC 2026-09-03): el POST /impersonate daba 201 pero
            // el hard-reload de abajo aterrizaba en /login. Causa: loginSuccess
            // (auth-slice.ts) NUNCA persiste — todo caller real (auth.saga.ts)
            // llama sessionService.saveSession() aparte; acá faltaba. Sin esto
            // el token vivia solo en Redux, que un `window.location.href` borra
            // por completo, y el boot siguiente no encontraba nada en localStorage.
            sessionService.saveSession({
                user: newUser,
                token: newToken,
                refreshToken: newRefreshToken,
            }, false);

            onClose();
            // Opcional: recargar página o redirigir al dashboard
            window.location.href = '/';
        } catch (err: any) {
            dispatch(setError(err.message || 'Error al iniciar impersonación'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleStopImpersonation = async () => {
        // SEC (2026-06-14): restaurar la sesión admin guardada (la ruta /admin-recovery
        // nunca existió). Mismo restore que el banner de MainLayout.
        if (originalAdminToken && originalAdminUser) {
            dispatch(loginSuccess({
                user: originalAdminUser,
                token: originalAdminToken,
                refreshToken: originalAdminRefreshToken,
            }));
            // Corrección de TINA-RDICTO (2026-09-03 14:38Z, letra sobre PR #1411):
            // el slot admin (isAdmin=true, admin-slice.ts:99) nunca se tocó durante
            // la impersonación -- adminLoginSuccess ya lo guardó ahí y sigue intacto.
            // saveSession(admin, false) de abajo escribía al slot de USUARIO regular
            // con los datos del admin, que es el slot equivocado. Lo que hay que
            // vaciar es el slot de usuario (el del target impersonado) para que,
            // tras el hard-reload, adminInitializeAuth restaure el admin desde su
            // propio slot -- nunca tocado -- en vez de /login.
            sessionService.clearSession(false);
        }

        // TINA-RDICTO (14:45Z) + TINA-RDIBACKEND (14:54Z, PR #1412 82afaba3):
        // clearSession solo limpia localStorage/USER_DATA -- no puede borrar la
        // cookie HttpOnly auth_token que el servidor le puso al target durante
        // el START. jwt.guard.ts lee esa cookie ANTES que el header, así que sin
        // este POST el super-admin sigue siendo "el target" ante el guard hasta
        // que la cookie expira. Best-effort: si el POST falla (red, 401 por
        // sesión ya vencida, etc.) igual seguimos -- la limpieza del cliente y
        // el redirect no dependen de esto, es defensa adicional del servidor.
        try {
            await impersonationApiService.stop();
        } catch (err) {
            console.error('No se pudo detener la impersonación del lado servidor', err);
        }

        dispatch(stopImpersonation());
        window.location.href = '/admin/dashboard';
    };

    if (!isOpen) return null;

    // Calcular la posición top: Header height (64px) + Trial banner if visible (40px)
    const topOffset = 64 + (showTrialBanner ? 40 : 0);

    return (
        <>
            {/* Backdrop localizado (solo sobre el contenido) */}
            <div
                className={cn(
                    "fixed inset-0 z-[90] bg-black/20 backdrop-blur-[2px] transition-opacity duration-300",
                    collapsed ? "left-16" : "left-64"
                )}
                style={{ top: `${topOffset}px` }}
                onClick={onClose}
            />

            {/* Flyout Panel */}
            <div
                className={cn(
                    "fixed bottom-0 z-[100] flex flex-col bg-white transition-all duration-300 ease-in-out border-r border-gray-200",
                    "w-80 lg:w-[400px]",
                    collapsed ? "left-16" : "left-64"
                )}
                style={{ top: `${topOffset}px` }}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {selectedTenant ? 'Seleccionar Usuario' : 'Cambiar Workspace'}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            {selectedTenant ? `Usuarios en ${selectedTenant.name}` : 'Busca un tenant para ver sus usuarios'}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Search */}
                {!selectedTenant && (
                    <div className="p-6 pb-0">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar tenant por nombre o subdominio..."
                                className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4 text-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    loadTenants(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isLoading && (
                        <div className="flex h-40 flex-col items-center justify-center space-y-3">
                            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                            <p className="text-sm font-medium text-gray-500">Cargando...</p>
                        </div>
                    )}

                    {!isLoading && !selectedTenant && (
                        <div className="space-y-3">
                            {tenants?.map((tenant) => (
                                <button
                                    key={tenant.id}
                                    onClick={() => handleSelectTenant(tenant)}
                                    className="group flex w-full items-center justify-between rounded-xl border border-gray-100 p-4 transition-all hover:bg-indigo-50 hover:border-indigo-100 cursor-pointer"
                                >
                                    <div className="flex items-center space-x-3 text-left">
                                        <div className="rounded-lg bg-gray-50 p-2 transition-colors group-hover:bg-white group-hover:text-indigo-600">
                                            <Building2 className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">{tenant.name}</p>
                                            <p className="text-xs text-gray-500">{tenant.subdomain}.inbomarket.com</p>
                                        </div>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-1 group-hover:text-indigo-500" />
                                </button>
                            ))}
                            {tenants?.length === 0 && !isLoading && (
                                <div className="py-10 text-center">
                                    <p className="text-sm text-gray-500">No se encontraron tenants</p>
                                </div>
                            )}
                        </div>
                    )}

                    {!isLoading && selectedTenant && (
                        <div className="space-y-3">
                            <button
                                onClick={() => setSelectedTenant(null)}
                                className="mb-4 flex items-center text-xs font-bold uppercase tracking-wider text-primary-600 hover:text-primary-700 cursor-pointer transition-colors"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Volver a Tenants
                            </button>

                            {tenantUsers?.map((user) => (
                                <div
                                    key={user.id}
                                    className="flex items-center justify-between rounded-xl border border-gray-100 p-4 transition-all hover:border-indigo-100"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold uppercase text-xs">
                                            {user.firstName?.[0] || ''}{user.lastName?.[0] || user.email?.[0] || 'U'}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">{user.firstName} {user.lastName}</p>
                                            <p className="text-xs text-gray-500 capitalize">{user.role?.replace(/_/g, ' ').toLowerCase()}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleImpersonate(user)}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-transform hover:scale-105 active:scale-95 cursor-pointer"
                                        title="Entrar a este espacio"
                                    >
                                        <LogIn className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}

                            {tenantUsers?.length === 0 && !isLoading && (
                                <div className="py-10 text-center">
                                    <p className="text-sm text-gray-500">No hay usuarios disponibles en este tenant</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {isImpersonating && (
                    <div className="border-t border-gray-100 p-6 bg-gray-50">
                        <button
                            onClick={handleStopImpersonation}
                            className="w-full rounded-xl bg-orange-500 py-3 text-sm font-bold text-white hover:bg-orange-600 transition-all"
                        >
                            Terminar Impersonación
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};
